import React, { FC, useEffect, useReducer, useRef } from 'react';

import { Clients } from '@ariksa/notification';
import {
  ReportingTargets,
  ReportsResponse,
  ReportTypes,
  ScheduleInterval,
} from '@ariksa/reporting';
import { Box, Center, Flex, HStack, RadioGroup, Stack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import {
  each,
  filter,
  find,
  findIndex,
  includes,
  isEmpty,
  toArray,
  without,
} from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';

import {
  getEnvironmentAccountIdsArray,
  getEnvironmentAccountIdsObject,
  PageHeaderWithIcon,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import {
  CancelButton,
  FormAction,
  PrimaryButton,
  RadioButtonGroup,
  Select,
} from 'components/DataEntry';
import { weekRangeOptions } from 'components/DataEntry/Button/utils';
import { WeekRangeButtonGroup } from 'components/DataEntry/Button/WeekRangeButtonGroup';
import { CustomDayPicker } from 'components/DataEntry/Date';
import { Form, FormSchema } from 'components/DataEntry/Form';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { selectUser } from 'containers/App/selectors';
import { formStyles } from 'containers/Reports/AllReports/Components/styles';
import {
  adminPreferenceOptions,
  cloudProviderMapping,
  frequencyOptions,
  months,
  reportFormatOptions,
  targetOptions,
  vulnerabilitySubTypeOptions,
  weekDaysOptions,
  weekNumberOptions,
} from 'containers/Reports/AllReports/Components/utils';
import { selectReports } from 'containers/Reports/AllReports/selectors';
import { actions } from 'containers/Reports/AllReports/slice';
import { renderRadioButton } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { useBlueprintsMapping } from 'containers/SharedState/hooks/useBlueprintsMapping';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { useSearchParams } from 'hooks/useSearchParams';
import { toTitleCase } from 'utils/string';

interface Props {
  currentRecord: ReportsResponse;
  isOpen: boolean;
  onClose: () => void;
  actionType: FormAction;
}

export const ReportForm: FC<Props> = props => {
  const { currentRecord, isOpen, onClose, actionType } = props;
  const { report, users } = useSelector(selectReports);
  const { info } = useSelector(selectUser);
  const { clients } = useSelector(selectSharedState);
  const { environmentMapping } = useEnvironmentOptions();
  const { getAccountOption } = useCloudAccountId();
  const { environmentId, accountId } = useAccessBoundary();
  const params = useSearchParams<{ reportType: string }>();
  const { updateValuesInURL } = useSearchParamFunctions();

  const currentDate = new Date();
  const dispatch = useDispatch();
  const ref = useRef(document.querySelector('.portal-container'));

  const {
    getOptions: getBlueprintOptions,
    envBlueprintMapping,
    alertingEnvBlueprintMapping,
  } = useBlueprintsMapping();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      environment: {},
      providers: [],
      reportTypeOptions: [],
      selectedReportType: {},
      blueprintOptions: [],
      selectedBlueprint: {},
      selectedBlueprints: [],
      frequency: frequencyOptions[0],
      startDate: currentDate,
      endDate: dayjs().add(1, 'month').toDate(),
      dayOfWeek: weekRangeOptions[1],
      yearlyDate: currentDate,
      name: '',
      dayOfMonth: 0,
      month: months[dayjs(currentDate).month()],
      isEdit: false,
      emailIds: [],
      userIds: [],
      environmentOptions: [],
      accountOptions: [],
      userOptions: [],
      selectedUsers: [],
      selectedEnvs: [],
      selectedAccounts: [],
      scheduledTime: '10:00',
      userIdOptions: [],
      monthlyType: ScheduleInterval.Monthly,
      monthlyWeekNumber: weekNumberOptions[0],
      monthlyWeekDay: weekDaysOptions[0],
      target: targetOptions[0],
      elasticSearchOptions: [],
      selectedElasticSearch: {},
      reportFormat: reportFormatOptions[1],
      adminPreference: {},
      vulnerabilitySubType: vulnerabilitySubTypeOptions[0]?.value,
    },
  );
  // Get the current year
  const currentYear = new Date().getFullYear();
  const endYear = currentYear + 20;
  const startMonth = new Date(currentYear, 0, 1);
  const endMonth = new Date(endYear, 11, 31); // December 31st of endYear

  useEffect(() => {
    dispatch(
      actions.getUsers({
        q: { organization: info.org_id },
        onSuccess: res => {
          updateState({
            userOptions: map(res?.users, o => ({
              label:
                o.first_name + ' ' + o?.last_name + ' (' + o?.username + ')',
              value: o.email,
              data: o,
            })),
          });
        },
      }),
    );
  }, [dispatch, info]);

  useEffect(() => {
    updateState({ isEdit: actionType === 'Update' });
  }, [actionType]);

  //set selected environment
  useEffect(() => {
    if (!!environmentId)
      updateState({ environment: environmentMapping?.[environmentId] });
  }, [environmentMapping, environmentId]);

  //set selected environment in edit mode
  useEffect(() => {
    if (state.isEdit && !isEmpty(currentRecord?.ids)) {
      updateState({
        environment:
          environmentMapping?.[currentRecord?.ids?.[0]?.environment_id!],
      });
    }
  }, [currentRecord, state.isEdit, environmentMapping]);

  //set account options, selected accounts and providers
  useEffect(() => {
    if (!state.environment) return;
    let options: Record<string, any>[] = [];
    let providers = new Set();
    if (!!accountId) {
      options = [getAccountOption(accountId)];
      const provider = cloudProviderMapping[options?.[0]?.data?.cloud_type];
      if (!!provider) providers.add(provider);
    } else {
      options = map(state.environment?.account_ids, o => getAccountOption(o));
      each(options, o => {
        const provider = cloudProviderMapping[o?.data?.cloud_type];
        if (!!provider) providers.add(provider);
      });
    }
    updateState({
      accountOptions: options,
    });
    if (!state.isEdit)
      updateState({
        selectedAccounts: options,
        providers: Array.from(providers),
      });
  }, [state.environment, accountId, getAccountOption, state.isEdit]);

  useEffect(() => {
    if (!environmentId || isEmpty(envBlueprintMapping)) return;
    let options = getBlueprintOptions(envBlueprintMapping?.[environmentId]);
    if (state.selectedReportType?.value === ReportTypes.Issues) {
      options = getBlueprintOptions(
        alertingEnvBlueprintMapping?.[environmentId],
      );
    }
    updateState({
      blueprintOptions: options,
      ...(state.selectedReportType?.value === ReportTypes.Compliance
        ? { selectedBlueprint: options?.[0] }
        : { selectedBlueprints: [] }),
    });
  }, [
    getBlueprintOptions,
    environmentId,
    envBlueprintMapping,
    alertingEnvBlueprintMapping,
    state.selectedReportType,
  ]);

  //set the current compliance standard when a report type is compliance
  useEffect(() => {
    if (!!state.isEdit) {
      if (state.selectedReportType?.value === ReportTypes.Compliance)
        updateState({
          selectedBlueprint: find(
            state.blueprintOptions,
            o => o.value === currentRecord?.report_config?.blueprint_id,
          ),
        });
    }
  }, [
    state.selectedReportType,
    currentRecord,
    state.blueprintOptions,
    state.isEdit,
  ]);

  //set the blueprints when a report type is issues
  useEffect(() => {
    if (!!state.isEdit) {
      if (state.selectedReportType?.value === ReportTypes.Issues)
        updateState({
          selectedBlueprints: filter(state.blueprintOptions, o =>
            includes(currentRecord?.report_config?.blueprint_ids, o?.value),
          ),
        });
    }
  }, [
    state.selectedReportType,
    currentRecord,
    state.blueprintOptions,
    state.isEdit,
  ]);

  useEffect(() => {
    if (state.isEdit) updateState({});
  }, [state.isEdit]);

  //set selected accounts in edit mode
  useEffect(() => {
    if (!state.isEdit) return;
    const ids = currentRecord?.ids?.[0]?.account_ids;
    const options = map(ids, o => getAccountOption(o));
    let providers = new Set();
    each(options, o =>
      providers.add(cloudProviderMapping[o?.data?.cloud_type!]),
    );
    updateState({
      selectedAccounts: options,
      providers: Array.from(providers),
    });
  }, [state.isEdit, getAccountOption, currentRecord]);

  //get report types and set report type options and selected report type
  useEffect(() => {
    if (!state.isEdit && !isEmpty(state.providers)) {
      dispatch(
        actions.getReportTypes({
          q: { providerType: state.providers },
          onSuccess: res => {
            const options: Record<string, any>[] = map(res, o => ({
              label: toTitleCase(o),
              value: o,
            }));
            updateState({
              reportTypeOptions: options,
              // Keep the report type deep-linked via ?reportType= instead of
              // overwriting it with the first option once types arrive.
              selectedReportType:
                find(options, o => o.value === params.reportType) ??
                options?.[0],
            });
          },
        }),
      );
    }
  }, [dispatch, state.isEdit, state.providers, params.reportType]);

  //set selected report type in edit mode
  useEffect(() => {
    if (!state.isEdit && !params.reportType) return;
    const option = {
      label: toTitleCase(params.reportType ?? currentRecord?.report_type),
      value: params.reportType ?? currentRecord?.report_type,
    };
    updateState({
      reportTypeOptions: option,
      selectedReportType: option,
    });
  }, [currentRecord, state.isEdit, params.reportType]);

  //set Target (SIEM or Email)
  useEffect(() => {
    // Parenthesised the fallback: `??` binds tighter than `?:`, so a saved
    // Email target was turned into SIEM whenever the record had a target.
    let target =
      find(targetOptions, o => o?.value === currentRecord?.target) ??
      (state.selectedReportType?.value === ReportTypes.DataClassification
        ? targetOptions[1]
        : targetOptions[0]);
    const reportFormat =
      target?.value === ReportingTargets.ElasticSearch
        ? reportFormatOptions[2]
        : state.selectedReportType?.value === ReportTypes.Inventory ||
          state.selectedReportType?.value ===
            ReportTypes.VulnerabilitiesDetailed ||
          state.selectedReportType?.value === ReportTypes.Issues
        ? reportFormatOptions[0]
        : reportFormatOptions[1];
    updateState({ target, reportFormat });
  }, [state.selectedReportType, state.isEdit, currentRecord]);

  useEffect(() => {
    if (state?.target?.value === ReportingTargets.ElasticSearch)
      dispatch(
        sharedStateActions.getClients({ clientName: Clients.Elasticsearch }),
      );
  }, [dispatch, state?.target]);

  //set elastic search options
  useEffect(() => {
    const options = map(clients.elasticsearch, o => ({
      label: o?.name,
      value: o?.uuid,
    }));
    updateState({
      elasticSearchOptions: options,
      selectedElasticSearch: options?.[0],
    });
  }, [clients.elasticsearch]);

  //set selected elastic search client in update mode
  useEffect(() => {
    if (state.isEdit)
      updateState({
        selectedElasticSearch: find(
          state.elasticSearchOptions,
          o => o?.value === currentRecord?.client_id,
        ),
      });
  }, [state.elasticSearchOptions, currentRecord, state.isEdit]);

  useEffect(() => {
    if (state.isEdit) {
      const scheduled_time = currentRecord?.scheduled_time?.split(':');
      const time = dayjs
        .utc()
        .hour(parseInt(scheduled_time[0]))
        .minute(parseInt(scheduled_time[1]))
        .local()
        .format('HH:mm');
      let monthlyType: Record<string, any> = {
        // Daily/weekly records have no day_of_month; dayjs().date(undefined)
        // is a getter returning a number, so `.toDate()` crashed edit mode.
        yearlyDate: (!!currentRecord?.day_of_month
          ? dayjs().date(currentRecord.day_of_month)
          : dayjs()
        ).toDate(),
        dayOfWeek:
          find(weekRangeOptions, o => o.value === currentRecord.day_of_week) ??
          weekRangeOptions[0],
      };
      let frequency = currentRecord.frequency;
      if (currentRecord.frequency === ScheduleInterval.NWeekly) {
        frequency = ScheduleInterval.Monthly;
        monthlyType = {
          monthlyType: ScheduleInterval.NWeekly,
          monthlyWeekNumber:
            find(
              weekNumberOptions,
              o => o.value === currentRecord?.day_of_month!,
            ) ?? {},
          monthlyWeekDay: find(
            weekDaysOptions,
            o => o.value === currentRecord?.day_of_week,
          ),
        };
      }
      const target =
        find(targetOptions, o => o?.value === currentRecord?.target) ??
        targetOptions[0];
      updateState({
        scheduledTime: time,
        emailIds: currentRecord.email_ids,
        frequency: find(frequencyOptions, o => o.value === frequency) ?? {},
        startDate: dayjs.utc(currentRecord?.start_date).local()?.toDate(),
        endDate: dayjs.utc(currentRecord?.end_date).local()?.toDate(),
        name: currentRecord?.name,
        vulnerabilitySubType:
          currentRecord?.report_config?.vuln_report_sub_type,
        //target: target,
        ...monthlyType,
        /*reportFormat:
          currentRecord?.target === ReportingTargets.ElasticSearch
            ? reportFormatOptions[2]
            : currentRecord.report_type === ReportTypes.Inventory
            ? reportFormatOptions[0]
            : reportFormatOptions[1],*/
      });
      if (state.isEdit && currentRecord.frequency === ScheduleInterval.Yearly) {
        updateState({
          yearlyDate: dayjs()
            .month(findIndex(months, o => o === currentRecord.month))
            .date(currentRecord?.day_of_month!)
            .toDate(),
        });
      }
    }
  }, [currentRecord, state.isEdit]);

  useEffect(() => {
    updateState({
      dayOfMonth: dayjs(state.yearlyDate).date(),
      month: months[dayjs(state.yearlyDate).month()],
    });
  }, [state.yearlyDate]);

  /*//set filters
  useEffect(() => {
    if (state.isEdit) {
      updateState({
        selectedEnvs: filter(state.environmentOptions, o =>
          includes(
            [
              ...map(currentRecord.ids, i => i.environment_id),
              currentRecord?.filters?.environment,
            ],
            o.value,
          ),
        ),
      });
    }
  }, [state.isEdit, state.environmentOptions, currentRecord]);*/

  /*const setSelectedAccounts = useCallback(
    ids => {
      const accounts: Record<string, any>[] = [];
      forEach(ids, o => {
        forEach(o?.account_ids, a => {
          accounts.push({
            label: toCloudAccountId(a),
            value: a + '_' + o?.environment_id,
            data: {
              accountId: a,
              environmentId: o?.environment_id,
            },
          });
        });
      });
      return accounts;
    },
    [toCloudAccountId],
  );

  //set selected accounts
  useEffect(() => {
    if (state.isEdit) {
      updateState({
        selectedAccounts: setSelectedAccounts(currentRecord.ids),
      });
    }
  }, [state.isEdit, currentRecord, setSelectedAccounts]);*/

  //set selected users in edit mode
  useEffect(() => {
    if (state.isEdit) {
      updateState({
        selectedUsers: filter(state.userOptions, o =>
          includes(currentRecord?.report_config?.admin_emails, o.value),
        ),
      });
    }
  }, [currentRecord, state.isEdit, state.userOptions]);

  //set admin preference in edit mode
  useEffect(() => {
    if (
      state.isEdit &&
      !!currentRecord?.report_config?.admin_notification_preference
    ) {
      updateState({
        adminPreference: find(
          adminPreferenceOptions,
          o =>
            o?.value ===
            currentRecord?.report_config?.admin_notification_preference,
        ),
      });
    }
  }, [currentRecord, state.isEdit]);

  const handleOnClose = () => {
    onClose();
    if (!!params.addReport) {
      updateValuesInURL({ addReport: undefined, reportType: undefined });
    }
  };

  const onConfirm = () => {
    const scheduledTime = state.scheduledTime?.split(':');
    const time = dayjs()
      .hour(parseInt(scheduledTime[0]))
      .minute(parseInt(scheduledTime[1]))
      .utc()
      .format('HH:mm');
    const startTime = dayjs(state.startDate)
      .hour(parseInt(scheduledTime[0]))
      .minute(parseInt(scheduledTime[1]))
      .utc()
      .format('YYYY-MM-DD');
    const endTime = dayjs(state.endDate)
      .hour(parseInt(scheduledTime[0]))
      .minute(parseInt(scheduledTime[1]))
      .utc()
      .format('YYYY-MM-DD');

    const fields = frequency => {
      switch (frequency) {
        case ScheduleInterval.Weekly:
          return { day_of_week: state.dayOfWeek.value };
        case ScheduleInterval.Monthly:
          return { day_of_month: state.dayOfMonth };
        case ScheduleInterval.Yearly:
          return { day_of_month: state.dayOfMonth, month: state.month };
        case ScheduleInterval.NWeekly:
          return {
            day_of_month: state.monthlyWeekNumber.value,
            day_of_week: state.monthlyWeekDay.value,
            frequency: ScheduleInterval.NWeekly,
          };
        default:
          return {};
      }
    };
    let accounts = getEnvironmentAccountIdsObject(state.selectedAccounts);
    const remainingEnvironments = without(
      map(state.selectedEnvs, o => o.value),
      ...Object.keys(accounts),
    );
    const ids = [
      ...map(remainingEnvironments, o => ({
        environment_id: o,
        account_ids: [],
      })),
      ...getEnvironmentAccountIdsArray(state.selectedAccounts),
    ];
    const siem =
      state?.target?.value === ReportingTargets.ElasticSearch
        ? {
            target: state?.target?.value,
            client_id: state.selectedElasticSearch?.value,
          }
        : {};

    const payload = {
      name: state.name,
      start_date: startTime,
      end_date: endTime,
      duration: state.isEdit ? currentRecord.duration : undefined,
      description: state.isEdit ? currentRecord.description : '',
      scheduled_time: time,
      frequency: state.frequency?.value,
      email_ids: state.emailIds,
      user_ids: map(state.userIds, o => o.value),
      ids: [
        {
          environment_id: state.environment?.id,
          account_ids: map(state.selectedAccounts, o => o?.value),
        },
      ],
      report_format: state.reportFormat?.value,
      ...fields(state.frequency.value),
      ...(state.frequency.value === ScheduleInterval.Monthly
        ? fields(state.monthlyType)
        : {}),
      ...siem,
      report_config: {
        blueprint_id: state.selectedBlueprint?.value,
        blueprint_ids: map(state.selectedBlueprints, o => o?.value),
        report_type: state.selectedReportType?.value,
        admin_emails: map(state.selectedUsers, o => o.value),
        admin_notification_preference: state.adminPreference?.value,
        vuln_report_sub_type:
          state.selectedReportType?.value ===
          ReportTypes.VulnerabilitiesDetailed
            ? state.vulnerabilitySubType
            : undefined,
      },
    };
    //console.log(payload);
    //return;

    const onSuccess = () => {
      handleOnClose();
      dispatch(actions.getReports({}));
    };

    if (!state.isEdit)
      dispatch(
        actions.addReport({
          q: {
            reportsCreate: {
              ...payload,
              only_diff:
                state.selectedReportType?.value ===
                ReportTypes.VulnerabilitiesSummary,
              report_type: state.selectedReportType?.value,
            },
          },
          onSuccess: onSuccess,
        }),
      );
    else
      !!currentRecord?.id &&
        dispatch(
          actions.updateReport({
            q: { reportsUpdate: payload, reportId: currentRecord?.id },
            onSuccess: onSuccess,
          }),
        );
  };

  const weekly: FormSchema =
    state.frequency.value === ScheduleInterval.Weekly
      ? {
          day_of_week: {
            type: 'custom',
            component: () => (
              <Center mb={3}>
                <WeekRangeButtonGroup
                  value={state.dayOfWeek}
                  onChange={value => updateState({ dayOfWeek: value })}
                />
              </Center>
            ),
          },
        }
      : {};

  const monthly: FormSchema =
    state.frequency.value === ScheduleInterval.Monthly
      ? {
          weekDay: {
            type: 'custom',
            component: () => (
              <Flex align="start">
                <RadioButtonGroup
                  options={[
                    {
                      label: (
                        <CustomDayPicker
                          dateProps={{
                            mode: 'single',
                            onSelect: selected => {
                              updateState({
                                yearlyDate: selected ?? currentDate,
                                monthlyType: ScheduleInterval.Monthly,
                              });
                            },
                            selected: state.yearlyDate,
                          }}
                        />
                      ),
                      value: ScheduleInterval.Monthly,
                    },
                    {
                      label: (
                        <HStack>
                          <Box>
                            <Select
                              options={weekNumberOptions}
                              value={state.monthlyWeekNumber}
                              onChange={selected =>
                                updateState({
                                  monthlyWeekNumber: selected,
                                  monthlyType: ScheduleInterval.NWeekly,
                                })
                              }
                            />
                          </Box>
                          <Select
                            options={weekDaysOptions}
                            value={state.monthlyWeekDay}
                            onChange={selected =>
                              updateState({
                                monthlyWeekDay: selected,
                                monthlyType: ScheduleInterval.NWeekly,
                              })
                            }
                          />
                        </HStack>
                      ),
                      value: ScheduleInterval.NWeekly,
                    },
                  ]}
                  value={state.monthlyType}
                  allowClickOnIndividual={true}
                  onChange={option =>
                    updateState({ monthlyType: option.value })
                  }
                  styles={{
                    label: {
                      onClick: () => {},
                    },
                  }}
                />
              </Flex>
            ),
          },
        }
      : {};

  const yearly: FormSchema =
    state.frequency.value === ScheduleInterval.Yearly
      ? {
          yearly: {
            type: 'dayPicker',
            dateProps: {
              fromDate: currentDate,
              mode: 'single',
              onSelect: selected => {
                updateState({
                  yearlyDate: selected ?? currentDate,
                });
              },
              selected: state.yearlyDate,
            },
          },
        }
      : {};

  const siem: FormSchema =
    state.target?.value === ReportingTargets.ElasticSearch
      ? {
          recipients: {
            type: 'react-select',
            label: 'Recipient',
            isRequired: true,
            options: state.elasticSearchOptions,
            value: state.selectedElasticSearch,
            onChange: value => {
              updateState({ selectedElasticSearch: value });
            },
            isDisabled: clients.loading,
            isLoading: clients.loading,
          },
        }
      : {
          report_format: {
            type: 'react-select',
            label: 'Report format',
            //isRequired: true,
            options: reportFormatOptions,
            value: state.reportFormat,
            onChange: selected => {
              updateState({ target: selected });
            },
            isDisabled: true,
          },
          email_ids: {
            type: 'emailIds',
            label: 'Recipients (press enter to add)',
            isRequired: true,
            value: state.emailIds,
            onChange: value => {
              updateState({ emailIds: value });
            },
            placeholder: 'Enter email Ids...',
          },
        };

  const accountField: FormSchema =
    !!accountId && !state.isEdit
      ? {
          account: {
            type: 'text',
            label: 'Account',
            value: state.selectedAccounts?.[0]?.label,
            isDisabled: true,
          },
        }
      : {
          account: {
            type: 'react-select',
            label: 'Select accounts',
            options: state.accountOptions,
            value: state.selectedAccounts,
            onChange: selected => {
              let providers = new Set();
              each(selected, o =>
                providers.add(cloudProviderMapping[o?.data?.cloud_type!]),
              );
              updateState({
                selectedAccounts: selected,
                providers: toArray(providers),
              });
            },
            isMulti: true,
            /*onClickGroupHeading: options =>
        updateState({
          selectedAccounts: unionBy(
            state.selectedAccounts,
            options,
            'value',
          ),
        }),*/
            showTotalSelected: true,
          },
        };

  const complianceStandards: FormSchema =
    state.selectedReportType?.value === ReportTypes.Compliance
      ? {
          blueprint_id: {
            type: 'react-select',
            options: state.blueprintOptions,
            label:
              state.selectedReportType?.value === ReportTypes.Issues
                ? 'Blueprint'
                : 'Compliance Standard',
            value: state.selectedBlueprint,
            onChange: v => updateState({ selectedBlueprint: v }),
            //isDisabled: true,
          },
        }
      : state.selectedReportType?.value === ReportTypes.Issues
      ? {
          blueprint_ids: {
            type: 'react-select',
            options: state.blueprintOptions,
            isMulti: true,
            label: 'Select Blueprint',
            value: state.selectedBlueprints,
            onChange: v => updateState({ selectedBlueprints: v }),
            showTotalSelected: true,
            showSelectedAtBottom: true,
            tooltip: 'If blueprint is missing, enable alerting from Policy Hub',
            //isDisabled: true,
          },
        }
      : state.selectedReportType?.value === ReportTypes.VulnerabilitiesDetailed
      ? {
          vulnerabilitySubType: {
            label: 'Select Report Configuration',
            type: 'custom-with-form-control',
            component: (
              <BorderedBox borderRadius={8} py={5} px={4}>
                <RadioGroup
                  value={state.vulnerabilitySubType}
                  onChange={v => updateState({ vulnerabilitySubType: v })}
                >
                  <Stack spacing={6}>
                    {map(vulnerabilitySubTypeOptions, o =>
                      renderRadioButton(o.label, o.subLabel, o.value),
                    )}
                  </Stack>
                </RadioGroup>
              </BorderedBox>
            ),
          },
        }
      : {};

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleOnClose}
      header={
        <PageHeaderWithIcon
          iconType={IconTypes.Clock}
          label={<Box>{actionType} Report</Box>}
        />
      }
      styles={{
        drawer: { size: 'xl' },
      }}
      body={
        <Form
          schema={{
            name: {
              type: 'text',
              label: 'Name',
              isRequired: true,
              defaultValue: currentRecord.name,
              onChange: value => updateState({ name: value }),
              autoFocus: true,
            },
            filters: {
              type: 'object',
              properties: {
                environment: {
                  type: 'text',
                  label: 'Environment',
                  value: state.environment?.name,
                  isDisabled: true,
                },
                ...accountField,
              },
            },
            reportType: {
              type: 'react-select',
              label: 'Report type',
              options: state.reportTypeOptions,
              isRequired: true,
              value: state.selectedReportType,
              onChange: selected => {
                updateState({ selectedReportType: selected });
              },
              isDisabled: state.isEdit,
            },
            ...complianceStandards,
            frequency: {
              type: 'object',
              label: 'Frequency',
              properties: {
                frequency: {
                  type: 'react-select',
                  options: frequencyOptions,
                  value: state.frequency,
                  onChange: option => {
                    let endDate = dayjs(state.startDate)
                      .add(1, 'month')
                      .toDate();
                    if (option.value === ScheduleInterval.Weekly)
                      endDate = dayjs(state.startDate).add(3, 'month').toDate();
                    if (option.value === ScheduleInterval.Monthly)
                      endDate = dayjs(state.startDate)
                        .add(12, 'month')
                        .toDate();
                    if (option.value === ScheduleInterval.Yearly)
                      endDate = dayjs(state.startDate).add(5, 'years').toDate();
                    updateState({ frequency: option, endDate });
                  },
                },
                ...weekly,
                ...monthly,
                ...yearly,
              },
              styles: {
                objectContainer: { align: 'start', isInline: true, spacing: 4 },
              },
            },
            scheduled_time: {
              type: 'timePicker',
              label: 'Scheduled time',
              isRequired: true,
              value: state.scheduledTime,
              onChange: value => {
                updateState({ scheduledTime: value });
              },
            },
            date: {
              type: 'object',
              properties: {
                start_date: {
                  type: 'dayPicker',
                  label: 'Start date',
                  dateProps: {
                    fromDate: currentDate,
                    mode: 'single',
                    onSelect: selected => {
                      updateState({ startDate: selected ?? currentDate });
                      if (dayjs(selected ?? currentDate).isAfter(state.endDate))
                        updateState({
                          endDate: dayjs(selected ?? currentDate)
                            .add(1, 'day')
                            .toDate(),
                        });
                    },
                    captionLayout: 'dropdown',
                    toMonth: endMonth,
                    fromMonth: startMonth,
                    selected: state.startDate,
                  },
                },
                end_date: {
                  type: 'dayPicker',
                  label: 'End date',
                  dateProps: {
                    fromDate: dayjs(state.startDate).add(1, 'day').toDate(),
                    captionLayout: 'dropdown',
                    toMonth: endMonth,
                    fromMonth: startMonth,
                    mode: 'single',
                    onSelect: selected =>
                      updateState({ endDate: selected ?? currentDate }),
                    selected: state.endDate,
                  },
                },
              },
            },
            target: {
              type: 'react-select',
              label: 'Target',
              //isRequired: true,
              options: targetOptions,
              value: state.target,
              onChange: selected => {
                updateState({
                  target: selected,
                  reportFormat:
                    selected?.value === ReportingTargets.ElasticSearch
                      ? reportFormatOptions[2]
                      : // Use the enum values: the 'vulnerability_details'
                      // literal never matched 'vulnerabilities_detailed', so
                      // switching back to Email picked PDF instead of CSV.
                      state.selectedReportType?.value ===
                          ReportTypes.Inventory ||
                        state.selectedReportType?.value ===
                          ReportTypes.Issues ||
                        state.selectedReportType?.value ===
                          ReportTypes.VulnerabilitiesDetailed
                      ? reportFormatOptions[0]
                      : reportFormatOptions[1],
                });
              },
              isDisabled:
                state.selectedReportType?.value !== ReportTypes.Inventory &&
                state.selectedReportType?.value !== ReportTypes.Issues &&
                state.selectedReportType?.value !==
                  ReportTypes.VulnerabilitiesDetailed &&
                state.selectedReportType?.value !==
                  ReportTypes.VulnerabilitiesSummary,
            },
            ...siem,
            user_to_notify: {
              type: 'react-select',
              label: 'Users to notify when reports are sent',
              options: state.userOptions,
              isLoading: users.isLoading,
              isMulti: true,
              value: state.selectedUsers,
              onChange: s => updateState({ selectedUsers: s }),
            },
            admin_notification_preference: {
              type: 'react-select',
              label: 'Users should be notified when',
              options: adminPreferenceOptions,
              value: state.adminPreference,
              onChange: s => updateState({ adminPreference: s }),
            },
          }}
          styles={formStyles}
          buttonOptions={{
            submit: {
              name: 'Confirm',
              isVisible: false,
            },
            reset: {
              name: 'Cancel',
              onClick: handleOnClose,
            },
          }}
          handleSubmit={onConfirm}
        />
      }
      footer={
        <HStack w="full" justify="space-between">
          <CancelButton onClick={handleOnClose}>CANCEL</CancelButton>
          <PrimaryButton
            onClick={onConfirm}
            isDisabled={
              report.isLoading ||
              !state.name ||
              (state.target?.value === ReportingTargets.Email &&
                isEmpty(state.emailIds)) ||
              (state.target?.value === ReportingTargets.ElasticSearch &&
                !state.selectedElasticSearch?.value)
            }
            isLoading={report.isLoading}
            tooltip={
              !state.name
                ? 'Please provide a name.'
                : state.target?.value === ReportingTargets.Email &&
                  isEmpty(state.emailIds)
                ? 'Please provide one or more recipients.'
                : ''
            }
          >
            CONFIRM
          </PrimaryButton>
        </HStack>
      }
    />
  );
};
